import type { DataNode } from 'antd/es/tree'
import { getCateCarInfoRequest } from '@/service/carTree'

// 扩展 DataNode 类型
interface ExtendedDataNode extends DataNode {
  id?: number;
  isCar?: boolean;
  hasSearch?: boolean;
}

//车辆在线状态
enum CarOnlineStatus {
  Online_AccOn = 1,
  Online_AccOff = 2,
  Offline = 3,
  Unknown = 4
}

// key 前缀常量
const CATE_KEY_PREFIX = 'cate';
const CAR_KEY_PREFIX = 'car';

/**
 * 生成分组节点的 key
 * @param id 分组 id
 * @returns 格式化的 key，如 'cate1'
 */
export const generateCateKey = (id: number | string): string => `${CATE_KEY_PREFIX}${id}`;

/**
 * 生成车辆节点的 key
 * @param id 车辆 id
 * @returns 格式化的 key，如 'car1'
 */
export const generateCarKey = (id: number | string): string => `${CAR_KEY_PREFIX}${id}`;

/**
 * 从 key 中解析出 id
 * @param key 节点 key
 * @returns 解析出的数字 id
 */
export const parseIdFromKey = (key: React.Key): number => {
  const keyStr = String(key);
  if (keyStr.startsWith(CATE_KEY_PREFIX)) {
    return Number(keyStr.slice(CATE_KEY_PREFIX.length));
  }
  if (keyStr.startsWith(CAR_KEY_PREFIX)) {
    return Number(keyStr.slice(CAR_KEY_PREFIX.length));
  }
  // 兼容旧数据，直接返回数字
  return Number(key);
};

/**
 * 判断 key 是否为分组节点的 key
 * @param key 节点 key
 * @returns 是否为分组节点
 */
export const isCateKey = (key: React.Key): boolean => String(key).startsWith(CATE_KEY_PREFIX);

/**
 * 判断 key 是否为车辆节点的 key
 * @param key 节点 key
 * @returns 是否为车辆节点
 */
export const isCarKey = (key: React.Key): boolean => String(key).startsWith(CAR_KEY_PREFIX);

/**
 * 处理分类数据,转换为树形结构
 * @param cateData 原始分类数据
 * @returns 处理后的树形数据
 */
export const handleCateData = (cateData: any[]): ExtendedDataNode[] => {
  const handleNode = (node: any): ExtendedDataNode => {
    // 删除 parentId 属性
    delete node.parentId;

    // 添加 key 字段，使用 cate 前缀 + id
    node.key = generateCateKey(node.id);

    // 设置 isLeaf 属性:只有当节点具有 isCar 字段时才设为叶子节点
    node.isLeaf = !!node.isCar;

    // 如果有子节点,递归处理
    if (node.children && Array.isArray(node.children)) {
      node.children = node.children.map(handleNode);
    }

    return node as ExtendedDataNode;
  };

  // 处理所有根节点
  return cateData.map(handleNode);
};

/**
 * 根据key查找树上对应的节点
 * @param treeData 树形数据
 * @param targetKey 目标key
 * @returns 找到的节点或null
 */
export const findNodeByKey = (treeData: ExtendedDataNode[], targetKey: React.Key): ExtendedDataNode | null => {
  for (const node of treeData) {
    if (node.key === targetKey) {
      return node;
    }
    // 如果有子节点,递归查找
    if (node.children && node.children.length > 0) {
      const found = findNodeByKey(node.children, targetKey);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/**
 * 在树中查找指定key的节点
 * @param treeData 树形数据
 * @param targetKey 目标key
 * @returns 找到的节点或null
 */
export const findNodeInTree = (treeData: ExtendedDataNode[], targetKey: React.Key): ExtendedDataNode | null => {
  for (const node of treeData) {
    if (node.key === targetKey) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(node.children, targetKey);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/**
 * 查找节点的父节点key
 * @param treeData 树形数据
 * @param targetKey 目标key
 * @param parentKey 父节点key(可选)
 * @returns 父节点key或null
 */
export const findParentKey = (treeData: ExtendedDataNode[], targetKey: React.Key, parentKey?: React.Key): React.Key | null => {
  for (const node of treeData) {
    if (node.key === targetKey) {
      return parentKey || null;
    }
    if (node.children && node.children.length > 0) {
      const found = findParentKey(node.children, targetKey, node.key);
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
};

/**
 * 检查指定节点是否有父节点被选中
 * @param nodeKey 节点key
 * @param selectedKeys 选中的keys
 * @param treeData 树形数据
 * @returns 是否有父节点被选中
 */
export const hasSelectedParent = (nodeKey: React.Key, selectedKeys: React.Key[], treeData: ExtendedDataNode[]): boolean => {
  const parentKey = findParentKey(treeData, nodeKey);
  if (parentKey && selectedKeys.includes(parentKey)) {
    return true;
  }
  // 递归检查祖先节点
  if (parentKey) {
    return hasSelectedParent(parentKey, selectedKeys, treeData);
  }
  return false;
};

/**
 * 获取所有可见的车辆节点
 * @param treeData 树形数据
 * @param expandedKeys 展开的节点keys
 * @returns 可见的车辆节点数组
 */
export const getAllVisibleCarNodes = (treeData: ExtendedDataNode[], expandedKeys: React.Key[]): ExtendedDataNode[] => {
  const visibleCarNodes: ExtendedDataNode[] = [];

  const traverseNodes = (nodes: ExtendedDataNode[]) => {
    nodes.forEach(node => {
      // 只添加车辆节点到可见节点列表
      if (node.isCar) {
        visibleCarNodes.push(node);
      }

      // 如果节点已展开且有子节点,递归遍历子节点
      if (expandedKeys.includes(node.key!) && node.children && node.children.length > 0) {
        traverseNodes(node.children);
      }
    });
  };

  traverseNodes(treeData);
  return visibleCarNodes;
};

//获取所有车辆节点
export const getAllCarNodes = (treeData: ExtendedDataNode[]): ExtendedDataNode[] => {
  const visibleCarNodes: ExtendedDataNode[] = [];

  const traverseNodes = (nodes: ExtendedDataNode[]) => {

    nodes.forEach(node => {
      // 只添加车辆节点到可见节点列表
      if (node.isCar) {
        visibleCarNodes.push(node);
      }

      // 如果节点已展开且有子节点,递归遍历子节点
      if (node.children && node.children.length > 0) {
        traverseNodes(node.children);
      }
    });
  };

  traverseNodes(treeData);
  return visibleCarNodes;
};

/**
 * 树形数据操作工具 hook
 * @param treeData 树形数据
 * @returns 工具方法集合
 */
export const useCarTreeUtils = (treeData: ExtendedDataNode[]) => {
  /**
   * 过滤选中节点,返回去重后的父节点优先的新数组
   * @param selectedKeys 选中的keys
   * @returns 过滤后的keys
   */
  const filterSelectedNodes = async (selectedKeys: React.Key[]): Promise<React.Key[]> => {
    const cars: ExtendedDataNode[] = [];
    // 第一步:所有车辆节点加到cars里面(isCar为true的节点)
    const nonCarKeys = selectedKeys.filter(key => {
      const node = findNodeInTree(treeData, key);
      if (node && node.isCar) {
        cars.push(node);
      }
      return node && !node.isCar;
    });

    // 第二步:如果一个节点的父节点已经在选中列表中,则移除该节点
    const filteredKeys: React.Key[] = [];

    for (const key of nonCarKeys) {
      const hasParentSelected = hasSelectedParent(key, nonCarKeys, treeData);
      if (!hasParentSelected) {
        filteredKeys.push(key);
      }
    }

    // 第三步:根据筛选的分组id查询车
    let queriedCarIds: number[] = [];
    if (filteredKeys.length > 0) {
      try {
        const cateIds = filteredKeys.map(key => parseIdFromKey(key));
        const response = await getCateCarInfoRequest(cateIds);
        if (response.data?.success && response.data?.data) {
          queriedCarIds = response.data.data;
        }
      } catch (error) {
        console.error('查询分组车辆失败:', error);
      }
    }

    // 第四步:把查询到的车和cars合并,注意去重，返回车辆的原始 id
    const existingCarIds = new Set(cars.map(car => car.id));
    const mergedCarIds: number[] = [...cars.map(car => car.id!)];

    queriedCarIds.forEach(carId => {
      if (!existingCarIds.has(carId)) {
        mergedCarIds.push(carId);
      }
    });

    return mergedCarIds;
  };

  /**
   * 过滤选中节点,返回完整的节点对象数组(包含车辆详细信息)
   * @param selectedKeys 选中的keys
   * @returns 过滤后的节点对象数组
   */
  const filterSelectedNodesWithDetails = async (selectedKeys: React.Key[]): Promise<ExtendedDataNode[]> => {
    const cars: ExtendedDataNode[] = [];
    // 第一步:所有车辆节点加到cars里面(isCar为true的节点)并筛选出选中的非车辆节点
    const nonCarKeys = selectedKeys.filter(key => {
      const node = findNodeInTree(treeData, key);
      if (node && node.isCar) {
        cars.push(node);
      }
      return node && !node.isCar;
    });

    // 第二步:如果一个节点的父节点已经在选中列表中,则移除该节点
    const filteredKeys: React.Key[] = [];

    for (const key of nonCarKeys) {
      const hasParentSelected = hasSelectedParent(key, nonCarKeys, treeData);
      if (!hasParentSelected) {
        filteredKeys.push(key);
      }
    }

    // 第三步:根据筛选的分组id查询车
    let queriedCarIds: number[] = [];
    if (filteredKeys.length > 0) {
      try {
        const cateIds = filteredKeys.map(key => parseIdFromKey(key));
        const response = await getCateCarInfoRequest(cateIds);
        if (response.data?.success && response.data?.data) {
          queriedCarIds = response.data.data;
        }
      } catch (error) {
        console.error('查询分组车辆失败:', error);
      }
    }

    // 第四步:把查询到的车和cars合并,注意去重
    const existingCarIds = new Set(cars.map(car => car.id));
    const mergedCars: ExtendedDataNode[] = [...cars];
   
    // 对于查询到的车辆ID,需要在树中查找对应的节点对象，使用 car 前缀的 key
    queriedCarIds.forEach(carId => {
      if (!existingCarIds.has(carId)) {
        const carNode = findNodeInTree(treeData, generateCarKey(carId));
        if (carNode) {
          mergedCars.push(carNode);
        }
      }
    });

    return mergedCars;
  };

  const isCarOnLine = (car: any): CarOnlineStatus => {
    if (car.jt808DeviceId) {
      if (car.jt808OnlineFlag || car.jt808IsOnline) {
        return car?.acc ? CarOnlineStatus.Online_AccOn : CarOnlineStatus.Online_AccOff;
      }
      return CarOnlineStatus.Offline;
    }


    return (car.onlineFlag || car.isOnline) ? CarOnlineStatus.Online_AccOn : CarOnlineStatus.Offline;
  }


  return {
    filterSelectedNodes,
    filterSelectedNodesWithDetails,
    isCarOnLine,
    findNodeByKey: (targetKey: React.Key) => findNodeByKey(treeData, targetKey),
    findNodeInTree: (targetKey: React.Key) => findNodeInTree(treeData, targetKey),
    findParentKey: (targetKey: React.Key, parentKey?: React.Key) => findParentKey(treeData, targetKey, parentKey),
    hasSelectedParent: (nodeKey: React.Key, selectedKeys: React.Key[]) => hasSelectedParent(nodeKey, selectedKeys, treeData),
    getAllVisibleCarNodes: (treeData: ExtendedDataNode[], expandedKeys: React.Key[]) => getAllVisibleCarNodes(treeData, expandedKeys),
    getAllCarNodes: (treeData: ExtendedDataNode[]) => getAllCarNodes(treeData),
  };
};

// 导出类型
export type { ExtendedDataNode };
export { CarOnlineStatus };
